import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { GENDERS } from 'components/intake/PatientInfoSection';
import Button from 'components/shared/Button';
import { useEnvironment } from 'context/environment';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import Workflow, { WorkflowAction } from 'models/Workflow';
import { createPortalAiRun } from 'services/api/portalAiRuns';
import {
  archivePortalScript,
  indexPortalScripts,
  portalScriptQueryKeys,
  showPortalScript,
} from 'services/api/portalScripts';
import { WorkflowValuesInput } from 'services/api/workflows';
import { uploadPortalDocuments } from 'services/portalDocuments';
import {
  finishManualPortal,
  launchManualPortal,
  ManualPortalField,
  onManualPortalResult,
  PortalRetrievalStatus,
  probePortalExtension,
  restartPortalRetrieval,
  runPortalRetrieval,
} from 'services/portalExtension';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { labelForValue } from './actionConfig';
import {
  ATTACHMENTS_VALUE_NAME,
  PORTAL_COLLECT_FIELDS,
  portalCollectionOutputs,
  sourceSystemLabel,
  VALUE_NAMES,
} from './constants';
import PortalCredentialsModal from './PortalCredentialsModal';
import RetrievePortalForm from './RetrievePortalForm';

interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
  onSaveProgress: (actionId: string, outputs: WorkflowValuesInput, options?: { narrationHint?: string }) => void;
  isSavingProgress: boolean;
}

// The value names whose retrieval outputs are documents rather than text — the engine's shared
// `attachments` value (captured via the extension's download marks, staged by the api as
// {id, filename} entries).
const DOCUMENT_OUTPUT_NAMES = [ATTACHMENTS_VALUE_NAME];

// Render a staged output value: a script's captures are usually strings, but stay defensive —
// arrays list out, documents read as their filenames, a collected facility as the portal string
// it carries, absent as an em dash.
function formatStagedValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return String(entry);
        const record = entry as { filename?: string; externalValue?: string; mappedValue?: string };
        return record.externalValue ?? record.mappedValue ?? record.filename ?? 'document';
      })
      .join(', ');
  }
  return String(value);
}

/**
 * The retrieval step's body, branched on the run's retrieval mode.
 * In priority order:
 *   1. staged outputs waiting on a person  -> confirm panel (review staged values)
 *   2. needs a person, extension/headless  -> browser-extension hand-off (the ladder:
 *                                             headless permits extension, so a headless
 *                                             run needing a person offers the attended
 *                                             flows, not just the bare form)
 *   3. needs a person, manual mode         -> manual form, plus a launch affordance
 *                                             when the extension is present (OLIO-2703)
 *   4. needs a person, any other mode      -> the plain manual form
 *   5. errored                             -> the manual form as recovery
 */
export default function PortalRetrievalBody(props: Props) {
  const { action, workflow, onComplete, isCompleting } = props;
  const mode = workflow.retrievalMode;

  if (action.isActionRequired && action.outputs.length > 0) return <ConfirmPanel {...props} />;

  if (action.isActionRequired && (mode === 'extension' || mode === 'headless')) return <HandOffFlow {...props} />;

  if (action.isActionRequired && mode === 'manual') return <ManualFlow {...props} />;

  if (action.isActionRequired) {
    return (
      <RetrievePortalForm action={action} workflow={workflow} onComplete={onComplete} isCompleting={isCompleting} />
    );
  }

  if (action.isErrored) {
    return (
      <Flow>
        <Muted data-testid='portal-retrieval-errored'>Automated retrieval failed — enter the details manually.</Muted>
        <RetrievePortalForm action={action} workflow={workflow} onComplete={onComplete} isCompleting={isCompleting} />
      </Flow>
    );
  }

  return null;
}

// The staged values a retrieval collected, waiting on a person's review — confirming completes
// the action with them as its outputs. Review only: a facility a script captured is the portal's
// own string, and resolving it is the mapping step's job, not this panel's.
function ConfirmPanel({ action, workflow, onComplete, isCompleting }: Props) {
  // Sent empty when the script captured no facility, so the mapping step still runs and asks.
  const confirm = () =>
    onComplete(action.id, {
      [VALUE_NAMES.FACILITIES]: [],
      ...action.stagedOutputs(),
    });

  return (
    <Flow data-testid='portal-confirm-panel'>
      <BodySmall>Review the retrieved information, then confirm to continue.</BodySmall>
      <Staged>
        {action.outputs.map((output) => (
          <SummaryRow key={output.name} data-testid={`staged-output-${output.name}`}>
            <SummaryName>{output.label ?? labelForValue(workflow, output.name)}</SummaryName>
            <SummaryValue>{formatStagedValue(output.value)}</SummaryValue>
          </SummaryRow>
        ))}
      </Staged>
      <div>
        <Button
          size='small'
          loading={isCompleting}
          disabled={isCompleting}
          onClick={confirm}
          data-testid='portal-confirm'>
          Confirm
        </Button>
      </div>
    </Flow>
  );
}

// The browser-extension hand-off: probe for the extension, then either replay the
// source system's saved script or record a fresh one against the portal link. The
// manual form stays one click away (and is the fallback whenever the hand-off can't run).
function HandOffFlow({ action, workflow, onComplete, isCompleting, onSaveProgress, isSavingProgress }: Props) {
  const { addToast } = useToastActions();
  const { environment } = useEnvironment();
  const queryClient = useQueryClient();
  const [probed, setProbed] = useState<boolean | null>(null);
  const [running, setRunning] = useState(false);
  const [recording, setRecording] = useState(false);
  const [aiRecording, setAiRecording] = useState(false);
  const [manual, setManual] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [liveStatus, setLiveStatus] = useState<PortalRetrievalStatus | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const sourceSystem = workflow.valueFor('source_system')?.value ?? '';
  const sourceLabel = sourceSystemLabel(sourceSystem);
  const retrievalPointer = workflow.valueFor('retrieval_pointer')?.value ?? '';
  const pointerIsUrl = /^https?:\/\//.test(retrievalPointer);

  useEffect(() => {
    let canceled = false;
    probePortalExtension().then((present) => {
      if (!canceled) setProbed(present);
    });
    return () => {
      canceled = true;
    };
  }, []);

  const scriptsQuery = useQuery({
    queryKey: portalScriptQueryKeys.list(sourceSystem),
    queryFn: ({ signal }) => indexPortalScripts(sourceSystem, signal),
    enabled: probed === true && !!sourceSystem,
  });
  const script = scriptsQuery.data?.[0];

  // Re-record: retire the saved script so this card falls back to its record paths.
  // Bodies are immutable (re-recording is a new row), so archiving is the replace —
  // and since the index serves only active rows, refetching the list is what flips the
  // primary button from "Retrieve from X" to "Record in X". Archiving happens up front
  // rather than after a successful recording: a recording the user abandons must not
  // leave the script they just rejected live for the next run.
  const reRecord = useMutation({
    mutationFn: (id: string) => archivePortalScript(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: portalScriptQueryKeys.list(sourceSystem) }),
    onError: () => addToast({ text: 'Could not replace the saved script.' }),
  });

  // Shared tail for every retrieval path: upload captured documents, guard the
  // document-first empty case, then complete or stage the values for confirmation.
  const handleValues = async (rawValues: Record<string, unknown>, documentOutputs: string[]) => {
    // Captured documents come back as base64 file entries — direct-upload them and
    // hand the api {signedId, filename} entries it can mint into Attachments. Captured
    // names pass through in whatever shape the script got them; the api resolves them.
    const values = await uploadPortalDocuments(rawValues, environment?.OLIO_FILES_DOMAIN);

    // Document-first: a run that captured nothing must not complete with an empty
    // packet (downstream extraction would have nothing to read) — fall back to the
    // manual upload form instead.
    if (documentOutputs.length > 0 && documentOutputs.every((name) => !(values[name] as unknown[])?.length)) {
      setFailure('The portal run captured no documents — the script may need re-recording.');
      setManual(true);
      return;
    }

    // Stage the values (save_progress); the refreshed run re-renders as the confirm panel.
    onSaveProgress(action.id, values);
  };

  const run = async () => {
    setFailure(null);
    setRunning(true);
    setRecording(!script);
    try {
      // The one field set every mode shares (record captures the collect-fields while the
      // human is already in the portal; replay shows the same rows, filled or not).
      const outputFields = portalCollectionOutputs(action.produces);
      const expectedOutputs = outputFields.map((output) => output.name);
      const documentOutputs = expectedOutputs.filter((name) => DOCUMENT_OUTPUT_NAMES.includes(name));
      const params = { patient_name: workflow.valueFor('portal_patient_name')?.value ?? '' };
      const context = { workflowId: workflow.id, actionId: action.id, sourceSystem, retrievalPointer };

      const onStatus = (status: PortalRetrievalStatus, id: string) => {
        setLiveStatus(status);
        setJobId(id);
      };

      let values: Record<string, unknown>;
      if (script) {
        const detail = await showPortalScript(script.id);
        values = await runPortalRetrieval(
          {
            script: { entryUrl: detail.entryUrl, body: detail.body, retrievalUrl: detail.retrievalUrl },
            // The retrieval pointer is always a resolved URL (validated at api ingress),
            // so it's the live entry point for replay — the extension prefers it over the
            // script's stale record-time entryUrl.
            entryUrl: retrievalPointer,
            params,
            context,
            expectedOutputs,
            documentOutputs,
            outputFields,
          },
          { onStatus }
        );
      } else {
        values = await runPortalRetrieval(
          {
            record: true,
            entryUrl: retrievalPointer,
            params,
            context,
            expectedOutputs,
            documentOutputs,
            outputFields,
          },
          { onStatus }
        );
      }

      await handleValues(values, documentOutputs);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Portal retrieval failed.';
      addToast({ text: message });
      setFailure(message);
      setManual(true);
    } finally {
      setRunning(false);
      setLiveStatus(null);
      setJobId(null);
    }
  };

  // Record with AI: create a run, then hand it to the extension, whose side-panel
  // conductor drives the AI turns. The AI's action sequence records as the script.
  const runWithAi = async () => {
    setFailure(null);
    setRunning(true);
    setRecording(true);
    setAiRecording(true);
    try {
      const collectOutputs = portalCollectionOutputs(action.produces);
      const expectedOutputs = collectOutputs.map((output) => output.name);
      const documentOutputs = expectedOutputs.filter((name) => DOCUMENT_OUTPUT_NAMES.includes(name));
      const availableParams = { patient_name: workflow.valueFor('portal_patient_name')?.value ?? '' };
      const context = { workflowId: workflow.id, actionId: action.id, sourceSystem, retrievalPointer };
      // Per-output shape comes from the collect declaration itself — no second source of truth
      // about which outputs are lists, or which are alternative shapes of one field.
      const goal = { outputs: collectOutputs };
      const onStatus = (status: PortalRetrievalStatus, id: string) => {
        setLiveStatus(status);
        setJobId(id);
      };

      const aiRun = await createPortalAiRun({ sourceSystem, goal, availableParams });
      const values = await runPortalRetrieval(
        {
          record: true,
          ai: true,
          runId: aiRun.id,
          entryUrl: retrievalPointer,
          params: availableParams,
          context,
          expectedOutputs,
          documentOutputs,
          outputFields: collectOutputs,
          goal,
          availableParams,
        },
        { onStatus }
      );
      await handleValues(values, documentOutputs);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Portal retrieval failed.';
      addToast({ text: message });
      setFailure(message);
      setManual(true);
    } finally {
      setRunning(false);
      setAiRecording(false);
      setLiveStatus(null);
      setJobId(null);
    }
  };

  const restart = () => {
    if (jobId) restartPortalRetrieval(jobId);
    // The extension reruns the state machine from the top; its broadcasts repopulate this.
    setLiveStatus('running');
  };

  // Probing — resolves within half a second, nothing to flash in the meantime.
  if (probed === null) return null;

  const manualForm = (
    <RetrievePortalForm action={action} workflow={workflow} onComplete={onComplete} isCompleting={isCompleting} />
  );

  if (probed === false) {
    return (
      <Flow>
        <Muted data-testid='portal-extension-missing'>
          Olio browser extension not detected — enter the details manually.
        </Muted>
        {manualForm}
      </Flow>
    );
  }

  if (manual) {
    const badCredentials = !!failure && failure.includes('credentials appear invalid');
    return (
      <Flow>
        {badCredentials && (
          <Muted data-testid='portal-credentials-invalid'>
            The portal rejected the saved credentials. Update them in the extension side panel&apos;s Portal logins
            section, then try again.
          </Muted>
        )}
        {failure && !badCredentials && <Muted data-testid='portal-retrieval-failure'>{failure}</Muted>}
        {manualForm}
      </Flow>
    );
  }

  // The job may take minutes — record mode runs as long as the human takes in the portal tab.
  if (running || isSavingProgress || isCompleting) {
    if (liveStatus === 'confused') {
      return (
        <Flow>
          <Muted data-testid='portal-retrieval-confused'>The portal session got confused.</Muted>
          <div>
            <Button size='small' onClick={restart} data-testid='portal-retrieval-restart'>
              Restart
            </Button>
          </div>
        </Flow>
      );
    }
    if (liveStatus === 'waiting_login') {
      return (
        <Muted data-testid='portal-retrieval-waiting-login'>
          Log in to the portal tab to continue — the retrieval resumes automatically.
        </Muted>
      );
    }
    const runningCopy = aiRecording
      ? 'The AI is navigating the portal — you can watch along in the portal tab…'
      : recording
        ? 'Retrieving from the portal — finish in the portal tab…'
        : 'Retrieving…';
    return <Muted data-testid='portal-retrieval-running'>{runningCopy}</Muted>;
  }

  if (scriptsQuery.isPending) return null;

  // Nothing to replay and nowhere to record — only the manual form can move this forward.
  if (!script && !pointerIsUrl) {
    return (
      <Flow>
        <Muted data-testid='portal-no-script'>No saved script and no portal link to record against.</Muted>
        {manualForm}
      </Flow>
    );
  }

  return (
    <Flow>
      <Actions>
        {/* Held while an archive is in flight: `show` serves archived scripts too, so a click
            landing mid-replace would replay the very script being retired. */}
        <Button size='small' onClick={run} disabled={reRecord.isPending} data-testid='portal-retrieve'>
          {script ? `Retrieve from ${sourceLabel}` : `Record in ${sourceLabel}`}
        </Button>
        {!script && (
          <Button size='small' variant='outline-primary' onClick={runWithAi} data-testid='portal-retrieve-ai'>
            Record with AI
          </Button>
        )}
      </Actions>
      <SecondaryLinks>
        <ManualLink type='button' onClick={() => setManual(true)} data-testid='portal-enter-manually'>
          enter manually instead
        </ManualLink>
        {/* Only offered against a saved script (with none, the card already records) and only
            when there's a portal link to record the replacement against — archiving without one
            strands the card on "no script and no portal link", with no way back to record mode. */}
        {script && pointerIsUrl && (
          <ManualLink
            type='button'
            onClick={() => reRecord.mutate(script.id)}
            disabled={reRecord.isPending}
            data-testid='portal-re-record'>
            {reRecord.isPending ? 'replacing…' : 're-record this script'}
          </ManualLink>
        )}
        {/* Headless prep (OLIO-2886): save the portal login automatic retrieval
            signs in with. Encrypted in the browser (OLIO-2906) — the modal owns
            the whole flow; nothing here ever sees the password. Only offered against
            a saved script: the save endpoint rejects credentials without one, so
            surfacing the form earlier would collect a password it can't store. */}
        {script && (
          <ManualLink type='button' onClick={() => setShowCredentials(true)} data-testid='portal-credentials-setup'>
            set up automatic sign-in
          </ManualLink>
        )}
      </SecondaryLinks>
      {showCredentials && <PortalCredentialsModal sourceSystem={sourceSystem} setShow={setShowCredentials} />}
    </Flow>
  );
}

// Manual mode (OLIO-2703): the manual form (documents + optional typed fields), plus a
// launch affordance. With the extension, the launch is a job: the portal tab gets the
// extension's manual-entry HUD, and whatever the person types there comes back and
// prefills this card's fields — the tab is the extension's to close (OLIO-2704).
// Without the extension, the same button is a plain window.open and the person types
// into this card's fields directly. Either way the form is the one completion path.
function ManualFlow({ action, workflow, onComplete, isCompleting }: Props) {
  const { addToast } = useToastActions();
  const [probed, setProbed] = useState<boolean | null>(null);
  const [launchId, setLaunchId] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);
  const [prefill, setPrefill] = useState<Record<string, unknown> | null>(null);
  const { facilities: allowedFacilities } = useAllowedFacilities();

  const sourceSystem = workflow.valueFor('source_system')?.value ?? '';
  const sourceLabel = sourceSystemLabel(sourceSystem);
  const retrievalPointer = workflow.valueFor('retrieval_pointer')?.value ?? '';
  const pointerIsUrl = /^https?:\/\//.test(retrievalPointer);

  useEffect(() => {
    let canceled = false;
    probePortalExtension().then((present) => {
      if (!canceled) setProbed(present);
    });
    return () => {
      canceled = true;
    };
  }, []);

  // The HUD's send (or the drafts a closed tab left behind) prefills the form fields,
  // and the launch re-arms — the tab is gone either way.
  useEffect(() => {
    if (!launchId) return;
    return onManualPortalResult(launchId, ({ values }) => {
      // Strings prefill their fields; the facilities arrive as a list and the collected name as
      // its own composite — neither is a string, so both need a clause of their own or the HUD's
      // send is dropped on the way to the form.
      const typed = Object.fromEntries(
        Object.entries(values).filter(
          ([name, value]) =>
            (typeof value === 'string' && value !== '') ||
            (name === VALUE_NAMES.FACILITIES && Array.isArray(value) && value.length > 0) ||
            (name === VALUE_NAMES.PATIENT_NAME && !!value && typeof value === 'object')
        )
      );
      if (Object.keys(typed).length > 0) setPrefill(typed);
      setLaunchId(null);
    });
  }, [launchId]);

  // Close the launched tab (if any) as the form completes — the completion path is the
  // same whether or not the user took the assisted route.
  const complete = (actionId: string, outputs: WorkflowValuesInput) => {
    if (launchId) finishManualPortal(launchId);
    onComplete(actionId, outputs);
  };

  const launch = async () => {
    // No extension: a plain new tab, and the fields below are the entry surface.
    if (!probed) {
      window.open(retrievalPointer, '_blank', 'noopener');
      return;
    }
    setLaunching(true);
    try {
      // Same declaration the form renders, described to the HUD: it has no idea what any field
      // means, so the datatype and the options it picks from travel with it. A facility is picked
      // there exactly as it is here — from this user's own facilities, arriving already mapped.
      const facilityOptions = allowedFacilities.map((group) => ({ value: group.id, label: group.name }));
      const fields: ManualPortalField[] = PORTAL_COLLECT_FIELDS.map(({ name, label, type, cardinality }) => ({
        name,
        label,
        cardinality,
        // The extension renders a fixed set of input kinds, and a formatted SSN box isn't one of
        // them — so anything it can't render is collected as text rather than widening its
        // contract to claim otherwise. Nothing in PORTAL_COLLECT_FIELDS is an SSN today.
        type: type === 'facility' ? 'select' : type === 'ssn' ? 'text' : type,
        ...(type === 'facility' ? { options: facilityOptions } : {}),
        ...(type === 'select' ? { options: GENDERS } : {}),
      }));
      const { launchId: id } = await launchManualPortal({
        entryUrl: retrievalPointer,
        expectedOutputs: fields.map((field) => field.name),
        fields,
        params: { patient_name: workflow.valueFor('portal_patient_name')?.value ?? '' },
        context: { workflowId: workflow.id, actionId: action.id, sourceSystem, retrievalPointer },
      });
      setLaunchId(id);
    } catch (error) {
      addToast({ text: error instanceof Error ? error.message : 'Could not open the portal tab.' });
    } finally {
      setLaunching(false);
    }
  };

  const manualForm = (
    <RetrievePortalForm
      action={action}
      workflow={workflow}
      onComplete={complete}
      isCompleting={isCompleting}
      prefill={prefill}
    />
  );

  // Probing — resolves within half a second, nothing to flash in the meantime.
  if (probed === null) return null;

  // No portal link to open: the plain form alone.
  if (!pointerIsUrl) return manualForm;

  return (
    <Flow>
      <div>
        <Button
          size='small'
          onClick={launch}
          loading={launching}
          disabled={!!launchId}
          data-testid='portal-manual-launch'>
          {launchId ? `Opened in ${sourceLabel}` : `Open in ${sourceLabel}`}
        </Button>
      </div>
      <Muted>
        {probed
          ? 'Open the portal and enter what you find there — or fill in the details below.'
          : 'Open the portal, then enter the details below.'}
      </Muted>
      {manualForm}
    </Flow>
  );
}

const Flow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Staged = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: 13px;
`;

const SummaryName = styled(BodySmall)`
  color: ${colors.black50};
  flex-shrink: 0;
`;

const SummaryValue = styled(BodySmall)`
  color: ${colors.black100};
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;

const SecondaryLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ManualLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font-size: 12px;
  text-decoration: underline;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;
